import axios from 'axios';
import Cookies from 'js-cookie';
// O modulo dispatchava 'notification/addNotification' e 'fighter/fetchFighters',
// que nao existem em lugar nenhum: o Vuex so logava "unknown action type" e a
// reconstrucao acontecia sem um unico aviso na tela. Fora de um componente nao
// ha `this.$toast`, entao o toast vem importado direto do plugin.
import { toast } from '@/plugins/toast';

const API_URL = process.env.VUE_APP_STRAPI_URL || process.env.VUE_APP_API_URL || 'http://localhost:3000';

const state = {
  deadFighters: [],
  availableRevivalItems: [],
  shouldShowPopup: false,
  targetFighter: null,
  cooldownUntil: null,
  loading: false,
  error: null,
  lastCheckTime: null
};

const mutations = {
  SET_DEAD_FIGHTERS(state, fighters) {
    state.deadFighters = fighters;
  },
  
  SET_AVAILABLE_REVIVAL_ITEMS(state, items) {
    state.availableRevivalItems = items;
  },
  
  SET_SHOULD_SHOW_POPUP(state, show) {
    state.shouldShowPopup = show;
  },
  
  SET_TARGET_FIGHTER(state, fighter) {
    state.targetFighter = fighter;
  },
  
  SET_COOLDOWN(state, until) {
    state.cooldownUntil = until;
  },
  
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  
  SET_ERROR(state, error) {
    state.error = error;
  },
  
  CLEAR_ERROR(state) {
    state.error = null;
  },
  
  REMOVE_DEAD_FIGHTER(state, fighterId) {
    state.deadFighters = state.deadFighters.filter(f => f.id !== fighterId);
  },
  
  UPDATE_REVIVAL_ITEM_USES(state, { itemId, remainingUses }) {
    const item = state.availableRevivalItems.find(i => i.id === itemId);
    if (item) {
      item.remainingUses = remainingUses;
      if (remainingUses <= 0) {
        state.availableRevivalItems = state.availableRevivalItems.filter(i => i.id !== itemId);
      }
    }
  },
  
  SET_LAST_CHECK_TIME(state, time) {
    state.lastCheckTime = time;
  }
};

const actions = {
  /**
   * Check for dead fighters and available revival items
   * Called on login and periodically
   */
  async checkRevivalStatus({ commit, rootState }) {
    try {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      const playerId = rootState.player?.playerData?.id;
      if (!playerId) {
        return;
      }

      // Get dead fighters
      const deadFightersResponse = await axios.get(`${API_URL}/api/fighters/dead/${playerId}`, {
        headers: {
          Authorization: `Bearer ${Cookies.get('jwt')}`
        }
      });

      const deadFighters = deadFightersResponse.data || [];
      commit('SET_DEAD_FIGHTERS', deadFighters);

      if (deadFighters.length > 0) {
        // Get available revival items from inventory
        const revivalItemsResponse = await axios.get(`${API_URL}/api/players/${playerId}/revival-items`, {
          headers: {
            Authorization: `Bearer ${Cookies.get('jwt')}`
          }
        });

        const revivalItems = revivalItemsResponse.data || [];
        commit('SET_AVAILABLE_REVIVAL_ITEMS', revivalItems);

        // Show popup if we have dead fighters and revival items
        // But check localStorage to avoid showing too frequently
        if (revivalItems.length > 0 && !checkAlreadyShownToday()) {
          commit('SET_SHOULD_SHOW_POPUP', true);
        }
      } else {
        commit('SET_AVAILABLE_REVIVAL_ITEMS', []);
        commit('SET_SHOULD_SHOW_POPUP', false);
      }
      
      commit('SET_LAST_CHECK_TIME', Date.now());
    } catch (error) {
      console.error('Error checking revival status:', error);
      commit('SET_ERROR', error.message || 'Erro ao verificar status de revival');
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Attempt to revive a fighter using a revival item
   */
  async attemptRevival({ commit, dispatch, rootState }, { fighterId, revivalItemId }) {
    try {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      // Set cooldown immediately
      const cooldownEnd = Date.now() + 5000; // 5 seconds
      commit('SET_COOLDOWN', cooldownEnd);
      
      const playerId = rootState.player?.playerData?.id;
      if (!playerId) {
        throw new Error('Player ID não encontrado');
      }

      const response = await axios.post(`${API_URL}/api/fighters/${fighterId}/attempt-revival`, {
        revivalItemId,
        playerId
      }, {
        headers: {
          Authorization: `Bearer ${Cookies.get('jwt')}`
        }
      });

      const result = response.data;
      
      if (result.success) {
        // Fighter was successfully revived
        commit('REMOVE_DEAD_FIGHTER', fighterId);
        
        // Update revival item quantity
        // O servidor devolve quantos usos sobraram; antes o cliente descontava 1
        // de um campo `quantity` que a rota nova nem tem.
        commit('UPDATE_REVIVAL_ITEM_USES', {
          itemId: revivalItemId,
          remainingUses: result.remainingUses ?? 0
        });
        
        // Add success notification
        toast.success(`🔬 ${result.fighterName || 'Fighter'} foi reconstruído com sucesso!`, 5000);

        // player/fetchPlayer e quem recarrega a lista de lutadores (o endpoint
        // /player/data/:userId ja popula `fighters`).
        await dispatch('player/fetchPlayer', null, { root: true });
        
      } else {
        // Revival failed but item was consumed
        // O servidor devolve quantos usos sobraram; antes o cliente descontava 1
        // de um campo `quantity` que a rota nova nem tem.
        commit('UPDATE_REVIVAL_ITEM_USES', {
          itemId: revivalItemId,
          remainingUses: result.remainingUses ?? 0
        });
        
        // Add failure notification
        toast.error(
          `💥 Reconstrução de ${result.fighterName || 'fighter'} falhou. ${result.message || 'Item foi consumido.'}`,
          5000
        );
      }
      
      // Hide popup if no more dead fighters or revival items
      if (state.deadFighters.length === 0 || state.availableRevivalItems.length === 0) {
        commit('SET_SHOULD_SHOW_POPUP', false);
      }
      
      return result;
      
    } catch (error) {
      console.error('Error attempting revival:', error);
      const message = error.response?.data?.message || error.message || 'Erro desconhecido ao tentar revival';
      
      commit('SET_ERROR', message);
      
      // Add error notification
      toast.error(`❌ Erro ao tentar reconstrução: ${message}`, 5000);
      
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Show revival popup for specific fighter (from Fighter Details)
   */
  showRevivalPopupForFighter({ commit, state }, fighter) {
    if (state.availableRevivalItems.length === 0) {
      // No revival items available, show message
      toast.warning(
        '⚠️ Você não possui items de revival. Adquira nanites de reconstrução na loja premium.',
        5000
      );
      return;
    }
    
    commit('SET_TARGET_FIGHTER', fighter);
    commit('SET_SHOULD_SHOW_POPUP', true);
  },

  /**
   * Close revival popup
   */
  closeRevivalPopup({ commit }) {
    commit('SET_SHOULD_SHOW_POPUP', false);
    commit('SET_TARGET_FIGHTER', null);
  },

  /**
   * Handle "don't show again today" option
   */
  dontShowRevivalToday({ commit }) {
    const today = new Date().toDateString();
    localStorage.setItem('revivalPopupDontShowToday', today);
    commit('SET_SHOULD_SHOW_POPUP', false);
  },

  /**
   * Clear error state
   */
  clearError({ commit }) {
    commit('CLEAR_ERROR');
  }
};

const getters = {
  /**
   * Should show revival popup
   */
  shouldShowRevivalPopup: (state) => {
    return state.shouldShowPopup && 
           state.deadFighters.length > 0 && 
           state.availableRevivalItems.length > 0;
  },
  
  /**
   * Is revival system on cooldown
   */
  isOnCooldown: (state) => {
    return state.cooldownUntil && Date.now() < state.cooldownUntil;
  },
  
  /**
   * Time left in cooldown (seconds)
   */
  cooldownTimeLeft: (state) => {
    if (!state.cooldownUntil || Date.now() >= state.cooldownUntil) {
      return 0;
    }
    return Math.ceil((state.cooldownUntil - Date.now()) / 1000);
  },
  
  /**
   * Current target fighter (for popup focus)
   */
  currentTargetFighter: (state) => {
    return state.targetFighter || (state.deadFighters.length > 0 ? state.deadFighters[0] : null);
  },
  
  /**
   * Has dead fighters
   */
  hasDeadFighters: (state) => {
    return state.deadFighters.length > 0;
  },
  
  /**
   * Has revival items
   */
  hasRevivalItems: (state) => {
    return state.availableRevivalItems.length > 0;
  },
  
  /**
   * Loading state
   */
  isLoading: (state) => {
    return state.loading;
  }
};

/**
 * Helper function to check if revival popup was already shown today
 */
function checkAlreadyShownToday() {
  const dontShowToday = localStorage.getItem('revivalPopupDontShowToday');
  const today = new Date().toDateString();
  
  return dontShowToday === today;
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};